import { EMPTY_STRING } from '@uplift/constants';
import { useCurrentuser, useCurrentuserRefetch } from '@uplift/contexts';
import { VALIDATION_FIRST_NAME, VALIDATION_LAST_NAME, VALIDATION_MIDDLE_NAME, VALIDATION_PREFERRED_NAME } from '@uplift/validations';
import * as Yup from 'yup';
import { useSaveProfile } from '../../hooks';
import { useUpdateSettings } from '../../hooks/use-update-settings.hook';
import { useSaveUser } from '../../hooks/use-user.hook';
import { useQueryClient } from '@tanstack/react-query';
import { useUserWithProfileQuery } from '@uplift/queries';

export type NameFormType = {
  firstName: string;
  middleName?: string;
  lastName: string;
  preferredName: string;
};

export const useNameForm = () => {
  const { onFormSubmitError, onFormSubmitSuccess } = useUpdateSettings();
  const me = useCurrentuser();
  const { data: { user, profile } } = useUserWithProfileQuery(me.id, { suspense: true });
  const queryClient = useQueryClient();
  const saveProfile = useSaveProfile();
  const saveUser = useSaveUser();
  const { refetchUser } = useCurrentuserRefetch();

  const nameFormik = () => {
    const initialValues: NameFormType = {
      firstName: user?.firstName ?? EMPTY_STRING,
      middleName: user?.middleName ?? EMPTY_STRING,
      lastName: user?.lastName ?? EMPTY_STRING,
      preferredName: profile?.preferredName ?? EMPTY_STRING
    };

    const validationSchema = Yup.object({
      firstName: VALIDATION_FIRST_NAME,
      middleName: VALIDATION_MIDDLE_NAME,
      lastName: VALIDATION_LAST_NAME,
      preferredName: VALIDATION_PREFERRED_NAME,
    });

    const onSubmit = async (values: NameFormType) => {
      try {
        const shouldUpdateProfile = values.preferredName !== profile.preferredName;

        if (shouldUpdateProfile) {
          await saveProfile({
            preferredName: values.preferredName
          });
        }

        const shouldUpdateUser =
          values.firstName !== user.firstName
          || values.middleName !== user.middleName
          || values.lastName !== user.lastName;

        if (shouldUpdateUser) {
          await saveUser(user.id, {
            firstName: values.firstName,
            middleName: values.middleName,
            lastName: values.lastName,
          });
          refetchUser();
        }
        queryClient.invalidateQueries({ queryKey: [ 'user', user.id, 'displayName' ] });
        onFormSubmitSuccess();
      } catch (error) {
        onFormSubmitError();
      }
    };

    return { initialValues, validationSchema, onSubmit };
  };

  return { nameFormik };
};
